import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import lookupService from '../services/lookupService';
import { darkColors, lightColors } from '../theme';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';
import { createCommonStyles } from '../theme/styles';
import { colors, getTableHeaderStyles, getActualTableRowBackground } from '../theme/colors';

const LookupTables = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const { isDarkMode } = useAppTheme();
  const customColors = isDarkMode ? darkColors : lightColors;
  const commonStyles = createCommonStyles(isDarkMode);
  const [lookupTables, setLookupTables] = useState([]);
  const [currentTable, setCurrentTable] = useState('');
  const [tableData, setTableData] = useState([]);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '' });

  // Stable fetchers
  const fetchLookupTables = useCallback(async () => {
    try {
             const response = await lookupService.getAvailableLookupTables();
      
             // Proveri da li je response array ili objekat sa tables poljem
       let tables = [];
       if (Array.isArray(response)) {
         tables = response;
       } else if (response && response.tables && Array.isArray(response.tables)) {
         tables = response.tables;
       } else if (response && Array.isArray(response.data)) {
         tables = response.data;
       }
       
       setLookupTables(tables);
       
               // Ne postavljamo currentTable na startu - korisnik mora da izabere
    } catch (err) {
      console.error('Error fetching lookup tables:', err);
      setError(err.message);
      setLookupTables([]); // Postavi prazan array kao fallback
    }
  }, []);



     useEffect(() => {
     fetchLookupTables();
   }, [fetchLookupTables]);

  useEffect(() => {
    if (currentTable && typeof currentTable === 'string' && currentTable.trim() !== '') {
      // Pozovi fetchTableData bez setovanja loading state-a da se izbegne refresh
      const fetchTableDataSilently = async () => {
        try {
          const response = await lookupService.getAllLookupValues(currentTable);
          setTableData(response);
        } catch (err) {
          console.error('Error fetching table data:', err);
          setError(err.message);
        }
      };
      
      fetchTableDataSilently();
    }
  }, [currentTable]);

  

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({ name: item.name || '' });
    } else {
      setEditingItem(null);
      setFormData({ name: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData({ name: '' });
  };

  const handleSubmit = async () => {
    try {
      if (editingItem) {
        await lookupService.updateLookupValue(currentTable, editingItem._id, formData);
      } else {
        await lookupService.createLookupValue(currentTable, formData);
      }
      handleCloseDialog();
      
      // Pozovi fetchTableData bez setovanja loading state-a da se izbegne refresh
      const fetchTableDataSilently = async () => {
        try {
          const response = await lookupService.getAllLookupValues(currentTable);
          setTableData(response);
        } catch (err) {
          console.error('Error fetching table data:', err);
          setError(err.message);
        }
      };
      
      fetchTableDataSilently();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Da li ste sigurni da želite da obrišete ovu vrednost?')) {
      try {
        await lookupService.deleteLookupValue(currentTable, id);
        
        // Pozovi fetchTableData bez setovanja loading state-a da se izbegne refresh
        const fetchTableDataSilently = async () => {
          try {
            const response = await lookupService.getAllLookupValues(currentTable);
            setTableData(response);
          } catch (err) {
            console.error('Error fetching table data:', err);
            setError(err.message);
          }
        };
        
        fetchTableDataSilently();
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handleTableChange = (event, newValue) => {
    setCurrentTable(newValue);
  };

  // Proveri da li je korisnik super admin
  if (!currentUser || currentUser.userType !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }



  return (
    <div>
   
      {error && (
        <Alert severity="error" sx={{ mb: 2, ...commonStyles.alert('error') }}>
          {error}
        </Alert>
      )}

             {/* Table Selection Dropdown */}
       {Array.isArray(lookupTables) && lookupTables.length > 0 ? (
         <Box sx={{ ...commonStyles.glassContainerMedium, mb: 3, p: 2 }}>
           <FormControl sx={{ minWidth: 300 }}>
             <InputLabel sx={commonStyles.textSecondary}>Izaberi lookup tabelu</InputLabel>
             <Select
               value={currentTable}
               label="Izaberi lookup tabelu"
               onChange={(e) => handleTableChange(e, e.target.value)}
               displayEmpty
               sx={commonStyles.selectField}
               MenuProps={{
                 PaperProps: {
                   sx: commonStyles.selectMenu
                 }
               }}
             >
               <MenuItem value="" sx={{ ...commonStyles.textMuted, fontStyle: 'italic' }}>
                 Izaberi lookup tabelu...
               </MenuItem>
               {lookupTables.map((table) => (
                 <MenuItem key={table} value={table}>
                   {table.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                 </MenuItem>
               ))}
             </Select>
           </FormControl>
         </Box>
       ) : (
         <Box sx={{ ...commonStyles.glassContainerMedium, mb: 3, p: 2, textAlign: 'center' }}>
           <Typography variant="body1" sx={commonStyles.textSecondary}>
             Nema dostupnih lookup tabela. Proverite backend konfiguraciju.
           </Typography>
         </Box>
       )}

               {/* Add Button */}
        {currentTable && (
          <Box display="flex" justifyContent="flex-start" mb={2}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={commonStyles.gradientButton}
            >
              Dodaj novu vrednost
            </Button>
          </Box>
        )}

               {/* Current Table Data */}
        {currentTable && (
         <Box sx={{ ...commonStyles.glassContainerMedium, overflow: 'hidden' }}>
        
           <TableContainer sx={{ backgroundColor: isDarkMode ? 'transparent' : commonStyles.tableContainer.backgroundColor }}>
             <Table>
               <TableHead>
                 <TableRow sx={{
                   background: colors.table.header.background,
                   '& .MuiTableCell-head': {
                     color: commonStyles.textPrimary.color,
                     background: getTableHeaderStyles(isDarkMode).cellBackground,
                     fontWeight: 600,
                     borderBottom: `1px solid ${customColors.border.light}`
                   }
                 }}>
                   <TableCell>Naziv</TableCell>
                   <TableCell>Datum kreiranja</TableCell>
                   <TableCell>Akcije</TableCell>
                 </TableRow>
               </TableHead>
               <TableBody>
                 {Array.isArray(tableData) && tableData.length > 0 ? (
                   tableData.map((item, index) => (
                     <TableRow key={item._id} sx={{
                       backgroundColor: getActualTableRowBackground(index, isDarkMode),
                       '& .MuiTableCell-body': {
                         borderBottom: `1px solid ${customColors.border.light}`,
                         color: commonStyles.textPrimary.color
                       }
                     }}>
                       <TableCell sx={commonStyles.textPrimary}>
                         <Typography variant="body1">
                           {item.name}
                         </Typography>
                       </TableCell>
                       <TableCell sx={commonStyles.textSecondary}>
                         <Typography variant="body2">
                           {new Date(item.createdAt || item.created_at).toLocaleDateString()}
                         </Typography>
                       </TableCell>
                       <TableCell>
                         <IconButton
                           size="small"
                           onClick={() => handleOpenDialog(item)}
                           title="Uredi"
                           sx={commonStyles.iconButton(customColors.primary.main)}
                         >
                           <EditIcon />
                         </IconButton>
                         <IconButton
                           size="small"
                           onClick={() => handleDelete(item._id)}
                           title="Obriši"
                           sx={commonStyles.iconButton(colors.actions.delete)}
                         >
                           <DeleteIcon />
                         </IconButton>
                       </TableCell>
                     </TableRow>
                   ))
                 ) : (
                   <TableRow>
                     <TableCell colSpan={3} align="center" sx={commonStyles.textMuted}>
                       <Typography variant="body2">
                         Nema podataka u ovoj tabeli
                       </Typography>
                     </TableCell>
                   </TableRow>
                 )}
               </TableBody>
             </Table>
           </TableContainer>
         </Box>
       )}

      {/* Add/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: commonStyles.dialogPaper
        }}
      >
        <DialogTitle sx={commonStyles.dialogTitle}>
          {editingItem ? 'Uredi vrednost' : 'Dodaj novu vrednost'}
        </DialogTitle>
        <DialogContent sx={{ ...commonStyles.dialogContent, pt: 2 }}>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Naziv"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              autoFocus
              sx={commonStyles.formField}


            />
          </Box>
        </DialogContent>
        <DialogActions sx={commonStyles.dialogActions}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            sx={commonStyles.cancelButton}
          >
            Otkaži
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!formData.name.trim()}
            sx={commonStyles.gradientButton}
          >
            {editingItem ? 'Ažuriraj' : 'Dodaj'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LookupTables;
